/*------------------------------------------------------------------------------
   About      : Create a new insight (epic DA-697, II-FE-3)

                `code` and `insightId` do not exist until the server returns
                them — `ii-derivation-service` derives both once on create, and
                a client-supplied value is REJECTED rather than ignored. So the
                flow is: fill the form → POST → navigate to the edit screen with
                the returned documentId. There is no local id to optimistically
                render.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Stack } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useGlobalConfig }             from 'context/GlobalContext'
import { ANALYTICS_EVENTS }            from 'framework/analytics/AnalyticsEvents'
import { selectCMSSelectedPilot,
         selectCMSSelectedPilotDocId } from 'store/dataReducers/cmsPilotSlice'

import { InsightApis }       from '../api/insightAPI'
import { useInsightCatalog } from '../hooks/useInsightCatalog'
import { InsightEditForm }   from '../components/InsightEditForm'
import { TemplateCriteriaBuilder } from '../components/TemplateCriteriaBuilder'
import { TagRecommendationsPanel } from '../components/TagRecommendationsPanel'
import { InsightCreateData,
         TaggedRecommendation } from '../types/insightTypes'
import type { BuiltValidationState } from '../utils/insightValidation'
import { CriteriaCondition,
         conditionsToCriteria } from '../utils/criteriaBuilder'

/** Sensible starting point for the v1 L1 scope (BILLING_CYCLE + TOTAL). */
const INITIAL_DATA : InsightCreateData = {
  measurementType : 'ELECTRIC',
  validityType    : 'BILLING_CYCLE',
  timeOfUse       : 'TOTAL',
  disabled        : false,
  templateString  : '',
}

export const InsightCreatePage = () => {

  const { rc }   = useGlobalConfig()
  const navigate = useNavigate()

  const selectedPilotDocId = useSelector(selectCMSSelectedPilotDocId)
  const selectedPilot      = useSelector(selectCMSSelectedPilot)
  const pilotId            = selectedPilot?.pilotId
  const { catalog }        = useInsightCatalog(rc)

  const [data, setData]         = useState<InsightCreateData>(INITIAL_DATA)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>()

  /* Reco tags are buffered until the record exists — `tagRecommendations` is
     keyed on `insightDocumentId`, and `createInsight` has no slot for relations.
     `createdDocumentId` is set only on the partial-failure path: the insight
     saved but its tags did not, so the form must lock rather than offer a
     second create that would duplicate the record. */
  const [pendingTags, setPendingTags] = useState<TaggedRecommendation[]>([])
  const [isTagging, setIsTagging]     = useState(false)
  const [tagError, setTagError]       = useState<string | undefined>()
  const [createdDocumentId, setCreatedDocumentId] = useState<string | undefined>()

  /* Criteria are authored as rows here exactly as on the edit screen. They were
     missing from create, which meant an insight could only ever get criteria on
     a second pass through the editor. There is no `unparsed` bucket on a new
     record — nothing has been stored to fail to parse yet. */
  const [conditions, setConditions] = useState<CriteriaCondition[]>([])

  const { criteria : builtCriteria, duplicateVariables } = useMemo(
    () => conditionsToCriteria(conditions, undefined, catalog?.criteriaGrammar),
    [conditions, catalog?.criteriaGrammar],
  )

  const dataWithCriteria = useMemo<InsightCreateData>(
    () => ({ ...data, templateCriteria : builtCriteria }),
    [data, builtCriteria],
  )

  /* Buffered tags belong to the project they were picked from. The project
     switcher lives in the shell above this page, so it can change underneath a
     half-filled form — and these would then be applied to an insight created in
     a different project. Cleared rather than carried over; the panel's list has
     already been re-scoped by the same change. */
  useEffect(() => { setPendingTags([]) }, [selectedPilotDocId])

  const handleChange = (patch: Partial<InsightCreateData>) => {
    setData((prev) => ({ ...prev, ...patch }))
  }

  const handleCreate = async (validation?: BuiltValidationState) => {
    if (!selectedPilotDocId) {
      setSaveError('No project selected.')
      return
    }

    // Two rows for one variable means the stored map silently keeps only the
    // last. Block rather than losing a criterion behind another — same guard the
    // edit screen applies.
    if (duplicateVariables.length) {
      setSaveError(
        `${duplicateVariables.join(', ')} is used by more than one criteria condition. ` +
        'Criteria are stored one predicate per variable — use a range instead.',
      )
      return
    }

    setIsSaving(true)
    setSaveError(undefined)
    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.CREATE_SUBMITTED, {
      insight_type : data.type,
    })

    const resp = await InsightApis.createInsight(rc, {
      utilityDocumentId : selectedPilotDocId,
      pilotId,
      data              : dataWithCriteria,
    })

    setIsSaving(false)

    // Two distinct failure shapes: a transport/RBAC failure lands in `error`,
    // while a validation rejection comes back 200 with a per-item error array.
    // Both have to surface, or a rejected create looks like a successful one.
    if (resp.error) {
      setSaveError(resp.error.message || 'Failed to create insight')
      rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.CREATE_FAILED)
      return
    }
    if (resp.data?.error?.length) {
      setSaveError(
        resp.data.error
          .flatMap((entry) => Object.entries(entry).map(([key, message]) => `${key}: ${message}`))
          .join('; '),
      )
      rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.CREATE_FAILED)
      return
    }

    const created = resp.data?.insight
    if (!created?.documentId) {
      setSaveError('The server accepted the insight but returned no record to open.')
      return
    }

    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.CREATE_SUCCEEDED, {
      insight_type : created.type,
    })

    /* Tags are a second call: `tagRecommendations` is keyed on
       `insightDocumentId` and `createInsight`'s payload has no slot for
       relations. So the record exists from here on — every path below must lead
       to the editor, never back to a form that would create a duplicate. */
    if (!pendingTags.length) {
      navigate(`/cms/interesting-insight/edit/${created.documentId}`)
      return
    }

    setIsTagging(true)
    const tagResp = await InsightApis.tagRecommendations(rc, {
      insightDocumentId         : created.documentId,
      recommendationDocumentIds : pendingTags.map((reco) => reco.documentId),
      // ADD, not the server's default SET — explicit, and correct if a future
      // create ever returns a record that already carries tags.
      mode                      : 'ADD',
      pilotId,
    })
    setIsTagging(false)

    const tagFailure = tagResp.error?.message
      || tagResp.data?.error
        ?.flatMap((entry) => Object.entries(entry).map(([key, message]) => `${key}: ${message}`))
        .join('; ')

    if (tagFailure) {
      // The insight is saved; only the tagging failed. Surfaced here rather than
      // navigating, because a redirect would drop the message and the author
      // would have no way to know the tags never applied.
      setCreatedDocumentId(created.documentId)
      setTagError(tagFailure)
      return
    }

    rc.analytics.trackEvent(ANALYTICS_EVENTS.NBI_INSIGHTS.RECO_TAGGED)
    navigate(`/cms/interesting-insight/edit/${created.documentId}`)
  }

  return (
    <Box sx={{ p : 3 }}>
      <Stack direction="row" sx={{ mb : 2 }}>
        <Button
          size="small" startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/cms/interesting-insight/list')}
        >
          Back to insights
        </Button>
      </Stack>

      {!selectedPilotDocId && (
        <Alert severity="warning" sx={{ mb : 2 }}>
          Select a project before creating an insight.
        </Alert>
      )}

      <Alert severity="info" sx={{ mb : 2 }}>
        The <code>code</code> and <code>insight ID</code> are derived by the server
        from the classification below, and become read-only once created.
      </Alert>

      {/* The insight exists but its tags do not. Creating again would duplicate
          the record, so the only way forward is the editor. */}
      {createdDocumentId && (
        <Alert
          severity="warning"
          sx={{ mb : 2 }}
          action={(
            <Button
              size="small" color="inherit"
              onClick={() => navigate(`/cms/interesting-insight/edit/${createdDocumentId}`)}
            >
              Open insight
            </Button>
          )}
        >
          The insight was created, but tagging its {pendingTags.length}{' '}
          recommendation{pendingTags.length === 1 ? '' : 's'} failed: {tagError} Open
          the insight to tag {pendingTags.length === 1 ? 'it' : 'them'} there — do
          not submit this form again, it would create a second insight.
        </Alert>
      )}

      <InsightEditForm
        mode="create"
        value={dataWithCriteria}
        onChange={handleChange}
        pilotId={pilotId}
        catalog={catalog}
        isSaving={isSaving || isTagging}
        /* Create is always "dirty": there is no stored record to differ from,
           so the button must not be gated on a diff. Once the record HAS been
           created it is gated on exactly that — a second submit would duplicate. */
        isDirty={!!selectedPilotDocId && !createdDocumentId}
        saveError={saveError}
        onSaveDraft={handleCreate}
        onSaveSubmit={handleCreate}
        criteriaSlot={(
          <TemplateCriteriaBuilder
            conditions={conditions}
            onChange={setConditions}
            catalog={catalog}
            insightType={data.type}
            templateString={data.templateString ?? ''}
            disabled={isSaving}
          />
        )}
        relatedSlot={selectedPilotDocId ? (
          <TagRecommendationsPanel
            utilityDocumentId={selectedPilotDocId}
            /* No `insight`: pending mode. Picks are buffered here and applied
               by `handleCreate` once the server has derived a documentId. */
            pendingTags={pendingTags}
            onPendingChange={setPendingTags}
            disabled={isSaving || isTagging || !!createdDocumentId}
          />
        ) : undefined}
      />
    </Box>
  )
}

export default InsightCreatePage
